<template>
  <div>
    <div v-if="!error" class="detail-wrapper">
      <SideNavigation
        :sideColumns="side_cols"
        :selectedIndex="colIdxSelected"
        @section-clicked="sideColClicked"
      />
      <div class="list-wrapper" v-on:scroll.passive="handleScroll">
        <div
          v-if="loading"
          class="d-flex justify-content-center align-items-center"
          style="height: 100%"
        >
          <b-spinner label="Loading..."></b-spinner>
        </div>
        <div
          v-if="json_data"
          class="text-left m-2 font-weight-bold fixed-title"
        >
          <h4>TAIR Person Detail</h4>
        </div>
        <BaseEntry
          v-for="(e, i) in entries"
          :selectedColIdx="colIdxSelected"
          :currIdx="i"
          :key="i"
          :content="e"
          :ref="`entry` + i"
        ></BaseEntry>
      </div>
    </div>
    <div v-else>
      <PageNotFound />
    </div>
  </div>
</template>

<script>
//http://localhost:8082/person?key=4602
import BaseEntry from "@/components/detail/BaseEntry";
import SideNavigation from "@/components/detail/SideNavigation";
import { mapGetters } from "vuex";
import PostsService from "@/services/PostsService";
import PageNotFound from "@/components/common/PageNotFound";

export default {
  name: "PersonDetail",
  components: {
    BaseEntry,
    SideNavigation,
    PageNotFound,
  },
  data() {
    return {
      json_data: null,
      side_cols: ["Summary"],
      loading: false,
      error: false,
      colIdxSelected: 0,
      entries: [],
      entryPosys: [],
      helpTexts: {},
      devBypass: false,
    };
  },

  async mounted() {
    document.title = "Person Detail";

    // Enable local UI-only development with ?mockPersonDetail or localStorage flag (dev builds only)
    const isDevBuild = process.env.NODE_ENV !== "production";
    const url = new URL(window.location.href);
    const hasMockQuery = url.searchParams.has("mockPersonDetail");
    const hasMockStorage =
      window.localStorage.getItem("mockPersonDetail") === "1";
    this.devBypass = isDevBuild && (hasMockQuery || hasMockStorage);

    if (this.devBypass) {
      this.initDevMock();
      return;
    }

    let jsonId = this.$route.query.key;
    if (jsonId) {
      let timestamp = new Date().getTime(); //cache-busting parameter
      try {
        let response = await PostsService.getPersonDetail({ key: jsonId });
        this.json_data = response.data;

        this.loading = true;
        this.processEntries();
        this.loading = false;
        document.title = `Person Detail`;
      } catch (error) {
        console.error("Error fetching person details: ", error);
        this.loading = false;
        this.error = true;
      }
    } else {
      this.loading = false;
      this.error = true;
    }
  },
  methods: {
    ...mapGetters({
      getCommunityId: "authProfile/getCommunityId",
      getIsTairCurator: "authProfile/getIsTairCurator",
    }),
    initDevMock() {
      // Simulate person detail data for local development
      this.loading = false;
      this.error = false;

      this.json_data = {
        communityId: "dev-123456",
        personId: "dev-789",
        firstName: "Jane",
        lastName: "Smith",
        orcidId: "0000-0002-1234-5678",
        email: "jane.smith@university.edu",
        displayEmail: "T", // Set to "T" to show email, "F" to hide
        institutionName: "University of California, Berkeley",
        region: "California",
        country: "USA",
      };

      this.loading = true;
      this.processEntries();
      this.loading = false;
      document.title = "Person Detail (Dev Mode)";
    },
    sideColClicked(colIdx) {
      this.scrollToElement(colIdx);
      this.colIdxSelected = colIdx;
    },
    processEntries() {
      this.side_cols.forEach((title) => {
        let entry = {
          title: title,
          name: title.replace(/\s/g, ""),
        };
        switch (title) {
          case "Summary":
            entry.type = "bands";
            entry.bands = this.fillSummary();
            break;
          default:
            entry.type = "bands";
            entry.bands = [];
            break;
        }
        this.entries.push(entry);
      });
    },
    fillSummary() {
      let bands = [];

      // Name with ORCID. This band is rendered with v-html, so escape the
      // user-entered name and the ORCID iD. (TAIR3-979)
      if (this.json_data.orcidId) {
        const esc = (v) =>
          String(v == null ? "" : v).replace(
            /[&<>"']/g,
            (c) =>
              ({
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': "&quot;",
                "'": "&#39;",
              }[c])
          );
        bands.push({
          key: "Name",
          type: "html",
          html: `${esc(this.json_data.firstName)} ${esc(this.json_data.lastName)} <a href="https://orcid.org/${encodeURIComponent(this.json_data.orcidId)}" target="_blank" rel="noopener noreferrer" style="text-decoration: none;"><img src="https://orcid.org/sites/default/files/images/orcid_16x16.png" alt="ORCID iD" style="width: 12px; height: 12px; margin-left: 4px; vertical-align: middle; display: inline-block;"></a>`,
        });
      } else {
        bands.push({
          key: "Name",
          text: this.json_data.firstName + " " + this.json_data.lastName,
        });
      }

      // Email (only if displayEmail is true)
      if (this.json_data.email && this.json_data.displayEmail === "T") {
        bands.push({
          key: "Email",
          text: this.json_data.email,
          type: "link",
          link: `mailto:${this.json_data.email}`,
        });
      }

      // Institution
      if (this.json_data.institutionName) {
        bands.push({
          key: "Institution",
          text: this.json_data.institutionName,
        });
      }

      // State/Province
      if (this.json_data.region) {
        bands.push({
          key: "State/Province",
          text: this.json_data.region,
        });
      }

      // Country/Region
      if (this.json_data.country) {
        bands.push({
          key: "Country/Region",
          text: this.json_data.country,
        });
      }
      if (
        this.json_data.communityId == this.getCommunityId() ||
        this.getIsTairCurator()
      ) {
        bands.push({
          key: "Update Record",
          html: `If you find this information to be inaccurate, or wish to add other information, please <a href="/edit/person?key=${this.json_data.communityId}" target="_blank">UPDATE YOUR RECORD HERE</a>`,
          type: "html",
        });
      }
      return bands;
    },

    //
    scrollToElement(colIdx) {
      const entryRef = this.$refs["entry" + colIdx];
      const el = entryRef[0].$el;
      if (el) {
        const top = el.offsetTop - 210;
        el.parentElement.scrollTo(0, top);
      }
    },
    handleScroll(e) {
      const scrollTop = e.target.scrollTop + 215;
      let currTopEntry = 0;
      this.entryPosys.forEach((e, i) => {
        if (scrollTop > e) {
          currTopEntry = i;
        }
      });
      this.colIdxSelected = currTopEntry;
    },
  },
};
</script>

<style scoped lang="scss">
.fixed-title {
  position: sticky;
  top: 0;
  background-color: #f1efec;
  z-index: 10;
  padding-left: 10px;
  padding-top: 10px;
}

.my-custom-scrollbar {
  position: relative;
  height: 200px;
  overflow: auto;
}
.table-wrapper-scroll-y {
  display: block;
}
</style>
